import React, { useState, useEffect, useMemo, useRef } from "react";
import { ModalWithIcon } from "../../RfModal/ModalWithIcon";
import { getWorkspacePlanLimits } from "../../../../../clientServices/workspaceLimits";
import { DowngradePlanTargets, WorkspaceUsage } from "../../../../../state/types";
import { isCreditSystemEnabled } from "../../../../routes/workspace/utils";
import workspaceSvc from "../../../../../clientServices/workspaceSvc";
import { getIncludedCredits } from "../../../../utils/ubpUtils";
import { usePlanInfo } from "../../../hooks/usePlanInfo";
import { isFreePlan } from "@functions/workspacePlans";
import { usePaidPlanSubscription } from "../../../hooks/usePaidPlanSubscription";
import { Plan } from "@functions/plans/types";
import CheckBox from "../../CheckBox";
import { PubliclyAvailablePlans } from "@functions/featureAvailability";
import InlineAlert from "../../InlineAlert";
import {
    motion,
    useMotionValue,
    useTransform,
    animate,
    easeIn,
    type Variants,
    AnimatePresence
} from "motion/react";
import { getValueInsights } from "../../../../../backendapi/valueInsightsApi";
import type {
    ValueInsightStats,
    AnnotatedImageSample
} from "@functions/services/valueInsights/valueInsightsTypes";
import { handleExtendTrial } from "../../../../../clientServices/trialService";
import Button from "../../Button";
import type { Annotation } from "../../ImageWithAnnotations";
// ImageWithAnnotations is a frontend-only React component being statically imported.
// When the backend functions runtime tries to load the import chain, it hits ImageWithAnnotations.tsx which has ES module exports, causing an error.
const ImageWithAnnotations = React.lazy(() =>
    import("../../ImageWithAnnotations").then((mod) => ({ default: mod.ImageWithAnnotations }))
);
import "./cancelPlanModal.scss";

type CancelPlanModalProps = {
    usage: WorkspaceUsage;
    isOpen: boolean;
    onClose: () => void;
    onCloseParentModal?: () => void;
    onNext: () => void;
    isDowngrade: boolean;
    expiredTrial?: boolean;
    selectedDowngradePlan: DowngradePlanTargets;
};

const MAX_NUMBER_OF_STATISTIC_SCREENS = 3; // Limit to 3 screens to avoid overwhelming the user; prioritize based on what we have data for

// Options for loader and steps transition
const LOADER_TRANSITION = { duration: 0.2, ease: easeIn };
const STEP_SCREEN_SLIDE_OFFSET = 80;

const STEP_SCREEN_SLIDE_VARIANTS: Variants = {
    enter: (direction: number) => ({
        x: direction > 0 ? STEP_SCREEN_SLIDE_OFFSET : -STEP_SCREEN_SLIDE_OFFSET,
        opacity: 0
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({
        x: direction > 0 ? -STEP_SCREEN_SLIDE_OFFSET : STEP_SCREEN_SLIDE_OFFSET,
        opacity: 0
    })
};

// Options for images fly-in animation
const IMAGE_FLYIN_FRONT_GROUP_DELAY = 0.2;
const IMAGE_FLYIN_BACKGROUND_BASE_DELAY = 0.6;
const IMAGE_FLYIN_BACKGROUND_RANDOM_RANGE = 0.6;

const seededRandom = (seed: number) => {
    const x = Math.sin(seed * 9301 + 49297) * 49297;
    return x - Math.floor(x);
};

const IMAGE_FLYIN_VARIANTS: Variants = {
    hidden: (index: number) => ({
        y: "-120%",
        opacity: 0,
        rotate: (seededRandom(index + 100) - 0.5) * 60
    }),
    visible: (index: number) => {
        const isFront = index < 3;
        return {
            y: 0,
            opacity: 1,
            rotate: 0,
            transition: {
                type: "spring",
                damping: 20,
                stiffness: 150,
                delay: isFront
                    ? IMAGE_FLYIN_FRONT_GROUP_DELAY
                    : IMAGE_FLYIN_BACKGROUND_BASE_DELAY +
                      seededRandom(index) * IMAGE_FLYIN_BACKGROUND_RANDOM_RANGE
            }
        };
    }
};

// Options for carousel animation
const CAROUSEL_IMAGE_SIZE = 9; // rem
const CAROUSEL_STEP_SIZE = CAROUSEL_IMAGE_SIZE + 0; // (image width + gap)rem
const CAROUSEL_BUFFER = 5; // How many images to render on either side of the focused image
const CAROUSEL_FOCUS_TIME = 3; // Seconds

// Options for best model stat animation
const MODEL_STAT_ANIMATION_TRANSITION = {
    delay: 0.5,
    duration: 2,
    ease: [0, 0.501, 0.489, 1] as const
};

// Options for inference stat animation
const INFERENCE_STAT_IMAGE_ANIMATION_TRANSITION = {
    delay: 0,
    duration: 0.5,
    ease: "easeInOut"
} as const;
const INFERENCE_STAT_BAR_OUTSET = 1; // px — how far the wrap path sits outside the image border
const INFERENCE_STAT_BAR_ANIMATION_TRANSITION = {
    delay: 0.5,
    duration: 1.25,
    ease: "easeInOut"
} as const;
const INFERENCE_ANNOTATION_COLORS = [
    "#8B5CF6",
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#EC4899",
    "#14B8A6",
    "#F97316"
];

// Options for dedicated deployments animation
const DEPLOYMENTS_RIPPLE_COUNT = 6;
const DEPLOYMENTS_RIPPLE_SPAWN_INTERVAL = 1; // Seconds between each ripple spawn
const DEPLOYMENTS_RIPPLE_SPEED = 1.5; // Multiplier: <1 = faster, >1 = slower
const DEPLOYMENTS_RIPPLE_DURATION =
    DEPLOYMENTS_RIPPLE_COUNT * DEPLOYMENTS_RIPPLE_SPAWN_INTERVAL * DEPLOYMENTS_RIPPLE_SPEED; // Total cycle so {DEPLOYMENTS_RIPPLE_COUNT} ripples are always visible
const DEPLOYMENTS_RIPPLE_START_DELAY_MAX = 5; // Seconds, max random stagger before first ripple per icon
const DEPLOYMENTS_MAX_DEPLOYMENT_ICONS = 10;
const DEPLOYMENTS_GRID_JITTER = 0.3; // Fraction of cell size; higher = more random, lower = more grid-like
const DEPLOYMENTS_VISIBLE_DURATION = 5; // Seconds the server stays visible before fading out
const DEPLOYMENTS_VISIBLE_TRANSITION_DURATION = 2; // Seconds for each fade out / fade in
const DEPLOYMENTS_INVISIBLE_HOLD_MIN = 2; // Seconds, min random time the server stays invisible before fading back in
const DEPLOYMENTS_INVISIBLE_HOLD_MAX = 5; // Seconds, max random time the server stays invisible before fading back in

const presentNumber = (value: number): string => {
    return value.toLocaleString();
};

interface ImagesFlyInProps {
    images: string[];
}

const ImagesFlyIn = ({ images }: ImagesFlyInProps) => (
    <div className="imagesFlyIn__container">
        {images.map((src, index) => (
            <motion.img
                key={`step_1-image-${index}`}
                src={src}
                alt=""
                className="imagesFlyIn__image"
                custom={index}
                variants={IMAGE_FLYIN_VARIANTS}
                initial="hidden"
                animate="visible"
            />
        ))}
    </div>
);

interface AnnotatedImageProps {
    sample: AnnotatedImageSample;
    className?: string;
    style?: React.CSSProperties;
}

const AnnotatedImage = ({ sample, className, style }: AnnotatedImageProps) => {
    const { url, width, height, boxes } = sample;

    const uniqueLabels = [...new Set(boxes.map((b) => b.label))];
    const ontology = uniqueLabels.map((name, index) => ({
        name,
        color: INFERENCE_ANNOTATION_COLORS[index % INFERENCE_ANNOTATION_COLORS.length]
    }));

    const annotations: Annotation[] = boxes
        .filter((b) => b.points.length > 0)
        .map((box, index) => ({
            id: String(index),
            class: box.label,
            type: "polygon" as const,
            vertices: [box.points.map(([x, y]) => ({ x, y }))]
        }));

    if (!width || !height || annotations.length === 0) {
        return <img src={url} className={className} style={style} alt="" aria-hidden="true" />;
    }

    return (
        <div className={className} style={style}>
            <React.Suspense fallback={<img src={url} alt="" aria-hidden="true" />}>
                <ImageWithAnnotations
                    src={url}
                    originalSize={{ width, height }}
                    ontology={ontology}
                    annotations={annotations}
                    fillContainer
                    showLabels
                />
            </React.Suspense>
        </div>
    );
};

interface ImageCarouselProps {
    images: AnnotatedImageSample[];
}

const ImageCarousel = ({ images }: ImageCarouselProps) => {
    const [currentIndex, setCurrentIndex] = useState(CAROUSEL_BUFFER);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => prev + 1);
        }, CAROUSEL_FOCUS_TIME * 1000);
        return () => clearInterval(interval);
    }, []);

    const start = Math.max(0, currentIndex - CAROUSEL_BUFFER);
    const end = currentIndex + CAROUSEL_BUFFER;
    const xOffset = `calc(50% - ${currentIndex * CAROUSEL_STEP_SIZE}rem - ${CAROUSEL_IMAGE_SIZE / 2}rem)`;

    return (
        <div className="imageCarousel__container">
            <motion.div
                className="imageCarousel__carouselStrip"
                initial={{ x: xOffset }}
                animate={{ x: xOffset }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
                {Array.from({ length: end - start + 1 }, (_, idx) => {
                    const index = start + idx;
                    const sample = images[index % images.length];
                    const isFocused = index === currentIndex;
                    return (
                        <AnnotatedImage
                            key={index}
                            sample={sample}
                            style={{ left: `${index * CAROUSEL_STEP_SIZE}rem` }}
                            className={`imageCarousel__carouselImage ${isFocused ? "imageCarousel__carouselImage--focused" : ""}`}
                        />
                    );
                })}
            </motion.div>
        </div>
    );
};

const AnimatedAccuracyNumber = ({ value }: { value: number }) => {
    const motionValue = useMotionValue(0);
    const display = useTransform(motionValue, (v) => v.toFixed(value === 100 ? 0 : 1));

    useEffect(() => {
        const controls = animate(motionValue, value, MODEL_STAT_ANIMATION_TRANSITION);
        return controls.stop;
    }, [value, motionValue]);

    return <motion.span>{display}</motion.span>;
};

interface BestModelStatisticProps {
    bestModel: any;
}

const BestModelStatistic = ({ bestModel }: BestModelStatisticProps) => {
    const modelName = bestModel?.projectName;
    const modelAccuracy = bestModel?.map ?? 0;
    return (
        <div className="modelStat__container">
            <div className="modelStat__card">
                <span className="modelStat__modelName">
                    Best Model:
                    <span>{modelName}</span>
                </span>
                <span className="modelStat__modelAccuracy">
                    <AnimatedAccuracyNumber value={modelAccuracy} />
                    <small>% mAP</small>
                </span>
                <div className="modelStat__barContainer">
                    <div className="modelStat__barTrack">
                        <motion.div
                            className="modelStat__barFill"
                            initial={{ width: "0%" }}
                            animate={{ width: `${modelAccuracy}%` }}
                            transition={MODEL_STAT_ANIMATION_TRANSITION}
                        />
                    </div>
                    <div className="modelStat__barLabels">
                        <span>
                            |<br />
                            okay
                        </span>
                        <span>
                            |<br />
                            good
                        </span>
                        <span>
                            |<br />
                            great
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface InferenceStatisticProps {
    images: AnnotatedImageSample[];
}

const InferenceStatistic = ({ images }: InferenceStatisticProps) => {
    const [showAnnotations, setShowAnnotations] = useState(false);
    const [paths, setPaths] = useState<{
        top: string;
        bottom: string;
        wrapTop: string;
        wrapBottom: string;
        leftFrac: number;
        wrapFrac: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const sample = useMemo(
        () => (images.length ? images[Math.floor(Math.random() * images.length)] : null),
        [images]
    );

    useEffect(() => {
        const timer = setTimeout(() => setShowAnnotations(true), 1000);
        return () => clearTimeout(timer);
    }, []);

    const computePaths = () => {
        if (!containerRef.current || !imageRef.current) return;
        const cr = containerRef.current.getBoundingClientRect();
        const ir = imageRef.current.getBoundingClientRect();
        const o = INFERENCE_STAT_BAR_OUTSET;
        const left = ir.left - cr.left - o;
        const right = ir.right - cr.left + o;
        const top = ir.top - cr.top - o;
        const bottom = ir.bottom - cr.top + o;
        const cy = (ir.top - cr.top + (ir.bottom - cr.top)) / 2;
        const W = cr.width;
        // Read border-radius from the image element so it always stays in sync
        const r = parseFloat(getComputedStyle(imageRef.current).borderRadius) || 0;

        // A rx ry x-rot large-arc sweep x y  (sweep 1=CW)
        // Outer entry/exit corners are sharp 90-degree angles; only image corners use arcs
        const topPath = [
            `M 0,${cy}`,
            `L ${left},${cy}`,
            `L ${left},${top + r}`,
            `A ${r} ${r} 0 0 1 ${left + r} ${top}`,
            `L ${right - r} ${top}`,
            `A ${r} ${r} 0 0 1 ${right} ${top + r}`,
            `L ${right},${cy}`,
            `L ${W},${cy}`
        ].join(" ");

        const bottomPath = [
            `M 0,${cy}`,
            `L ${left},${cy}`,
            `L ${left},${bottom - r}`,
            `A ${r} ${r} 0 0 0 ${left + r} ${bottom}`,
            `L ${right - r} ${bottom}`,
            `A ${r} ${r} 0 0 0 ${right} ${bottom - r}`,
            `L ${right},${cy}`,
            `L ${W},${cy}`
        ].join(" ");

        // Wrap-only paths (no horizontal tails) for the thicker overlay stroke
        const wrapTop = [
            `M ${left},${cy}`,
            `L ${left},${top + r}`,
            `A ${r} ${r} 0 0 1 ${left + r} ${top}`,
            `L ${right - r} ${top}`,
            `A ${r} ${r} 0 0 1 ${right} ${top + r}`,
            `L ${right},${cy}`
        ].join(" ");

        const wrapBottom = [
            `M ${left},${cy}`,
            `L ${left},${bottom - r}`,
            `A ${r} ${r} 0 0 0 ${left + r} ${bottom}`,
            `L ${right - r} ${bottom}`,
            `A ${r} ${r} 0 0 0 ${right} ${bottom - r}`,
            `L ${right},${cy}`
        ].join(" ");

        // Compute length fractions for timing the thick overlay animation
        const arcLen = (Math.PI * r) / 2;
        const vertLen = cy - top - 2 * r;
        const horizLen = right - left - 2 * r;
        const leftLen = left;
        const rightLen = W - right;
        const wrapLen = 4 * arcLen + 2 * vertLen + horizLen;
        const totalLen = leftLen + wrapLen + rightLen;
        const leftFrac = totalLen > 0 ? leftLen / totalLen : 0;
        const wrapFrac = totalLen > 0 ? wrapLen / totalLen : 1;

        setPaths({ top: topPath, bottom: bottomPath, wrapTop, wrapBottom, leftFrac, wrapFrac });
    };

    return (
        <div ref={containerRef} className="inferenceStat__container">
            {sample && (
                <>
                    <img
                        ref={imageRef}
                        src={sample.url}
                        onLoad={computePaths}
                        alt=""
                        aria-hidden="true"
                    />
                    <motion.div
                        className="inferenceStat__annotatedImageWrapper"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: showAnnotations ? 1 : 0 }}
                        transition={INFERENCE_STAT_IMAGE_ANIMATION_TRANSITION}
                    >
                        <AnnotatedImage sample={sample} />
                    </motion.div>
                    {paths && (
                        <svg className="inferenceStat__barSvg">
                            {[paths.top, paths.bottom].map((d, i) => (
                                <path key={`track-${i}`} d={d} fill="none" opacity={0.2} />
                            ))}
                            {[paths.top, paths.bottom].map((d, i) => (
                                <motion.path
                                    key={i}
                                    d={d}
                                    fill="none"
                                    pathLength={1}
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={INFERENCE_STAT_BAR_ANIMATION_TRANSITION}
                                />
                            ))}
                            {[paths.wrapTop, paths.wrapBottom].map((d, i) => (
                                <motion.path
                                    key={`wrap-${i}`}
                                    d={d}
                                    fill="none"
                                    strokeWidth={2.5}
                                    pathLength={1}
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{
                                        delay:
                                            INFERENCE_STAT_BAR_ANIMATION_TRANSITION.delay +
                                            INFERENCE_STAT_BAR_ANIMATION_TRANSITION.duration *
                                                paths.leftFrac,
                                        duration:
                                            INFERENCE_STAT_BAR_ANIMATION_TRANSITION.duration *
                                            paths.wrapFrac,
                                        ease: INFERENCE_STAT_BAR_ANIMATION_TRANSITION.ease
                                    }}
                                />
                            ))}
                        </svg>
                    )}
                </>
            )}
        </div>
    );
};

interface DedicatedDeploymentsProps {
    numberOfDeployments: number;
}

const DedicatedDeployments = ({ numberOfDeployments }: DedicatedDeploymentsProps) => {
    const count = Math.min(numberOfDeployments, DEPLOYMENTS_MAX_DEPLOYMENT_ICONS);

    const positions = useMemo(() => {
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);
        const cellW = 100 / cols;
        const cellH = 100 / rows;
        return Array.from({ length: count }, (_, index) => {
            const col = index % cols;
            const row = Math.floor(index / cols);
            return {
                x:
                    col * cellW +
                    cellW * (0.5 + (Math.random() - 0.5) * (1 - DEPLOYMENTS_GRID_JITTER)),
                y:
                    row * cellH +
                    cellH * (0.5 + (Math.random() - 0.5) * (1 - DEPLOYMENTS_GRID_JITTER)),
                startDelay: Math.random() * DEPLOYMENTS_RIPPLE_START_DELAY_MAX,
                invisibleHold:
                    DEPLOYMENTS_INVISIBLE_HOLD_MIN +
                    Math.random() *
                        (DEPLOYMENTS_INVISIBLE_HOLD_MAX - DEPLOYMENTS_INVISIBLE_HOLD_MIN)
            };
        });
    }, [count]);

    return (
        <div className="dedicatedDeployments__container">
            {positions.map((pos, index) => {
                const fadeCycle =
                    DEPLOYMENTS_VISIBLE_DURATION +
                    DEPLOYMENTS_VISIBLE_TRANSITION_DURATION * 2 +
                    pos.invisibleHold;
                return (
                    <motion.div
                        key={index}
                        className="dedicatedDeployments__server"
                        style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                        animate={{ opacity: [1, 1, 0, 0, 1] }}
                        transition={{
                            duration: fadeCycle,
                            times: [
                                0,
                                DEPLOYMENTS_VISIBLE_DURATION / fadeCycle,
                                (DEPLOYMENTS_VISIBLE_DURATION +
                                    DEPLOYMENTS_VISIBLE_TRANSITION_DURATION) /
                                    fadeCycle,
                                (DEPLOYMENTS_VISIBLE_DURATION +
                                    DEPLOYMENTS_VISIBLE_TRANSITION_DURATION +
                                    pos.invisibleHold) /
                                    fadeCycle,
                                1
                            ],
                            ease: "easeInOut",
                            repeat: Infinity,
                            delay: pos.startDelay
                        }}
                    >
                        {Array.from({ length: DEPLOYMENTS_RIPPLE_COUNT }, (_, r) => (
                            <div
                                key={r}
                                className="dedicatedDeployments__ripple"
                                style={{
                                    animationDuration: `${DEPLOYMENTS_RIPPLE_DURATION}s`,
                                    animationDelay: `${pos.startDelay + (r * DEPLOYMENTS_RIPPLE_DURATION) / DEPLOYMENTS_RIPPLE_COUNT}s`
                                }}
                            />
                        ))}
                        <i className="fa-regular fa-server" />
                    </motion.div>
                );
            })}
        </div>
    );
};

interface UsageStatItemProps {
    icon: string;
    label: string;
    value: string;
}

const UsageStatItem = ({ icon, label, value }: UsageStatItemProps) => (
    <div className="flex items-center justify-between gap-2 py-1">
        <span className="flex items-center gap-2 text-sm text-gray-600">
            <i className={icon}></i>
            {label}
        </span>
        <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
);

interface ModalStepsProps {
    numberOfSteps: number;
    currentStep: number;
    onStepClick?: (step: number) => void;
    checkedBoxes: Array<boolean>;
}

const ModalStepsControl = ({
    numberOfSteps,
    currentStep,
    onStepClick,
    checkedBoxes
}: ModalStepsProps) => {
    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (onStepClick) {
                onStepClick(index);
            }
        }
    };

    return (
        <div className="modalStepsControl">
            {Array.from({ length: numberOfSteps }, (_, index) => {
                let faIcon = "fa-circle"; // Default for future steps
                let iconColor = "text-divider";

                if (index < currentStep) {
                    // Completed steps
                    faIcon = "fa-circle-check";
                    iconColor = "text-tertiary";
                } else if (index === currentStep) {
                    // Current step
                    faIcon = "fa-circle-dot";
                    iconColor = "text-tertiary";
                }

                const isStepCheckboxChecked = !!checkedBoxes[index];
                const isPrioStepCheckboxChecked = index > 0 && !!checkedBoxes[index - 1];
                const isStepCurrentlyInView = index === currentStep;

                const canClickStep =
                    (isStepCheckboxChecked && !isStepCurrentlyInView) || isPrioStepCheckboxChecked;

                return (
                    <i
                        key={`step_control-${index}`}
                        className={`fa-solid ${faIcon} ${iconColor} step_control step_control--${canClickStep ? "clickable" : "non-clickable"}`}
                        role="button"
                        tabIndex={canClickStep ? 0 : -1}
                        aria-disabled={!canClickStep}
                        aria-label={`Step ${index + 1} of ${numberOfSteps}`}
                        onKeyDown={(event) => canClickStep && handleKeyDown(event, index)}
                        onClick={canClickStep && onStepClick ? () => onStepClick(index) : undefined}
                    />
                );
            })}
        </div>
    );
};

type UserStatisticView = {
    screenName: string;
    heading: string;
    subheading: string;
    cancelButtonText: string;
    cancelButtonAction: () => void;
    headerContent: React.ReactNode;
    checkboxText: string;
    relativeEventData: Record<string, any>;
};

const getRelevantUserStatisticScreens = ({
    churnStats,
    onFinalStepAction,
    workspaceId,
    currentPlan,
    selectedDowngradePlan,
    isDowngrade
}: {
    churnStats: ValueInsightStats | null;
    onFinalStepAction: () => void;
    workspaceId: string;
    currentPlan: WorkspaceUsage["plan"];
    selectedDowngradePlan: DowngradePlanTargets;
    isDowngrade: boolean;
}): UserStatisticView[] => {
    if (!churnStats) return [];

    const sampleImages = churnStats?.sampleImageUrls || [];
    const annotatedImages = churnStats?.sampleAnnotatedImages || [];
    const numberOfProjects = churnStats?.projects.totalCount || 0;
    const numberOfUploadedImages = churnStats?.images.totalUploaded || 0;
    const numberOfAnnotations = churnStats?.annotations.totalCount || 0;
    const numberOfTrainedModels = churnStats?.models.totalTrained || 0;
    const totalTrainingTimeInMinutes = churnStats?.models.totalTrainingTimeMinutes || 0;
    const numberOfInferences = churnStats?.inference.totalUsed || 0;
    const numberOfDeployments = churnStats?.deployments.dedicatedCount || 0;
    const inferencePercentile = churnStats?.inferencePercentile;
    const bestModel = churnStats?.models.bestModel;

    const screensToReturn: UserStatisticView[] = [];

    // Screen 1: Uploaded images
    if (sampleImages.length > 0) {
        // Each photo is ~0.24 mm thick
        const PHOTO_THICKNESS_MM = 0.24;
        // A quick way to determine whether to use imperial or metric units based on the user's locale:
        const isUsingImperialUnits = ["en-US", "en-LR", "en-MM"].includes(navigator.language);
        const stackHeightInMM = numberOfUploadedImages * PHOTO_THICKNESS_MM;
        const convertedStackHeight = isUsingImperialUnits
            ? Math.ceil(stackHeightInMM / 304.8) // mm to feet
            : Math.ceil(stackHeightInMM / 1000); // mm to meters
        const unit = isUsingImperialUnits
            ? convertedStackHeight === 1
                ? "foot"
                : "feet"
            : convertedStackHeight === 1
              ? "meter"
              : "meters";

        screensToReturn.push({
            screenName: "uploaded-images",
            heading: `${presentNumber(numberOfUploadedImages)} images uploaded across ${presentNumber(numberOfProjects)} projects`,
            subheading: `That’s a stack of photos almost ${presentNumber(convertedStackHeight)} ${unit} tall.`,
            cancelButtonText: "View Projects",
            cancelButtonAction: () => {
                eventTracker.event({
                    type: "Churn Navigated Away",
                    data: {
                        workspaceId,
                        currentPlan,
                        selectedDowngradePlan,
                        isDowngrade,
                        screen: "uploaded-images"
                    }
                });
                window.page(`/${workspaceId}/projects`);
            },
            headerContent: <ImagesFlyIn images={sampleImages.slice(0, 14)} />,
            checkboxText: "I understand that all data and models must be made public.",
            relativeEventData: { numberOfUploadedImages, numberOfProjects }
        });
    }

    // Screen 2: Annotations
    if (numberOfAnnotations > 0 && annotatedImages.length > 0) {
        // Annotations to time; Assuming each annotation takes 2 seconds
        const annotationTime = Math.ceil((numberOfAnnotations * 2) / 3600);

        screensToReturn.push({
            screenName: "annotations-created",
            heading: `${presentNumber(numberOfAnnotations)} annotations created`,
            subheading: `That’s over ${presentNumber(annotationTime)} hours of work, if labeled manually.`,
            cancelButtonText: "View Dataset Library",
            cancelButtonAction: () => {
                eventTracker.event({
                    type: "Churn Navigated Away",
                    data: {
                        workspaceId,
                        currentPlan,
                        selectedDowngradePlan,
                        isDowngrade,
                        screen: "annotations-created"
                    }
                });
                window.page(`/${workspaceId}/projects?tab=asset-library`);
            },
            headerContent: <ImageCarousel images={annotatedImages} />,
            checkboxText: "I understand I will have less features after downgrading.",
            relativeEventData: { numberOfAnnotations }
        });
    }

    // Screen 3: Trained models
    if (numberOfTrainedModels > 0 && bestModel?.projectUrl && bestModel?.modelId) {
        // Train time to movie time; Assuming the Titanic movie is 195 minutes long
        const totalTrainingTimeInHours = Math.ceil(totalTrainingTimeInMinutes / 60);
        const titanicEquivalency = Math.ceil(totalTrainingTimeInMinutes / 195);

        screensToReturn.push({
            screenName: "best-model",
            heading: `${presentNumber(numberOfTrainedModels)} ${numberOfTrainedModels === 1 ? "model" : "models"} trained over ${presentNumber(totalTrainingTimeInHours)} hours.`,
            subheading: `That’s the equivalent of watching Titanic almost ${presentNumber(titanicEquivalency)} ${titanicEquivalency === 1 ? "time" : "times"}.`,
            cancelButtonText: "View Best Model",
            cancelButtonAction: () => {
                eventTracker.event({
                    type: "Churn Navigated Away",
                    data: {
                        workspaceId,
                        currentPlan,
                        selectedDowngradePlan,
                        isDowngrade,
                        screen: "best-model"
                    }
                });
                window.page(
                    `/${workspaceId}/${bestModel?.projectUrl}/models/${bestModel?.projectUrl}/${bestModel?.modelId}`
                );
            },
            headerContent: <BestModelStatistic bestModel={bestModel} />,
            checkboxText: "I understand I will lose access to model evaluation.",
            relativeEventData: {
                numberOfTrainedModels,
                totalTrainingTimeInMinutes,
                bestModel: bestModel
            }
        });
    }

    // Screen 4: Inference usage
    if (
        numberOfInferences > 0 &&
        inferencePercentile !== undefined &&
        screensToReturn.length < MAX_NUMBER_OF_STATISTIC_SCREENS
    ) {
        screensToReturn.push({
            screenName: "inferences-run",
            heading: `Inferences run on ${presentNumber(numberOfInferences)} images`,
            subheading: `Top ${presentNumber(inferencePercentile)}% of Roboflow users.`,
            cancelButtonText: "View Usage",
            cancelButtonAction: () => {
                eventTracker.event({
                    type: "Churn Navigated Away",
                    data: {
                        workspaceId,
                        currentPlan,
                        selectedDowngradePlan,
                        isDowngrade,
                        screen: "inferences-run"
                    }
                });
                window.page(`/${workspaceId}/settings/usage`);
            },
            headerContent: <InferenceStatistic images={annotatedImages} />,
            checkboxText: "I understand I will no longer have access to model monitoring.",
            relativeEventData: { numberOfInferences, inferencePercentile }
        });
    }

    // Screen 5: Dedicated deployments
    if (numberOfDeployments > 0 && screensToReturn.length < MAX_NUMBER_OF_STATISTIC_SCREENS) {
        screensToReturn.push({
            screenName: "dedicated-deployments",
            heading: `${presentNumber(numberOfDeployments)} dedicated deployments`,
            subheading: "Production-ready infrastructure you won’t have anymore.",
            cancelButtonText: "View Dedicated Deployments",
            cancelButtonAction: () => {
                eventTracker.event({
                    type: "Churn Navigated Away",
                    data: {
                        workspaceId,
                        currentPlan,
                        selectedDowngradePlan,
                        isDowngrade,
                        screen: "dedicated-deployments"
                    }
                });
                window.page(`/${workspaceId}/deployments`);
            },
            headerContent: <DedicatedDeployments numberOfDeployments={numberOfDeployments} />,
            checkboxText: "I understand I will lose access to dedicated deployments.",
            relativeEventData: { numberOfDeployments }
        });
    }

    // Shuffle the screens order and assign 1-based screen numbers
    for (let iteration = screensToReturn.length - 1; iteration > 0; iteration--) {
        const randomScreenIndex = Math.floor(Math.random() * (iteration + 1));
        [screensToReturn[iteration], screensToReturn[randomScreenIndex]] = [
            screensToReturn[randomScreenIndex],
            screensToReturn[iteration]
        ];
    }
    for (let i = 0; i < screensToReturn.length; i++) {
        screensToReturn[i].relativeEventData.screenNumber = i + 1;
    }

    return screensToReturn;
};

interface LegacyPlanWarningProps {
    currentPlanName: string;
}

const LegacyPlanWarning = ({ currentPlanName }: LegacyPlanWarningProps) => (
    <>
        <InlineAlert level="danger" type="primary">
            <div>
                The {currentPlanName} is no longer available.{" "}
                <span className="font-bold">
                    You will not be able to enroll in this plan again.
                </span>{" "}
                If you choose to subscribe again later, you'll need to choose a publicly available
                plan, which may differ in pricing, limits, and features.
            </div>
        </InlineAlert>
        <br />
    </>
);

interface UsersStatisticsBodyProps {
    screens: UserStatisticView[];
    onFinalStepAction: () => void;
    workspaceId: string;
    currentPlan: WorkspaceUsage["plan"];
    selectedDowngradePlan: DowngradePlanTargets;
    isDowngrade: boolean;
    canExtendTrial: boolean;
    onExtendTrial: () => Promise<void>;
    onClose: () => void;
    onCloseParentModal?: () => void;
}

const UserStatisticsFlow = ({
    screens,
    onFinalStepAction,
    workspaceId,
    currentPlan,
    selectedDowngradePlan,
    isDowngrade,
    canExtendTrial,
    onExtendTrial,
    onClose,
    onCloseParentModal
}: UsersStatisticsBodyProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [direction, setDirection] = useState(1);
    const lastViewedStepRef = useRef(-1);
    const [checkedBoxes, setCheckedBoxes] = useState<boolean[]>([]);
    const [isExtendingTrial, setIsExtendingTrial] = useState(false);

    const goToStep = (step: number) => {
        eventTracker.event({
            type:
                step > currentStep
                    ? "Churn Statistic Next Button Clicked"
                    : "Churn Statistic Previous Button Clicked",
            data: {
                workspaceId,
                currentPlan,
                selectedDowngradePlan,
                isDowngrade,
                fromScreen: screens[currentStep].screenName,
                toScreen: screens[step].screenName
            }
        });

        setDirection(step > currentStep ? 1 : -1);
        setCurrentStep(step);
    };

    const handleClickNext = () => {
        if (currentStep === screens.length - 1) {
            eventTracker.event({
                type: "Churn Statistic Next Button Clicked",
                data: {
                    workspaceId,
                    currentPlan,
                    selectedDowngradePlan,
                    isDowngrade,
                    screen: screens[currentStep].screenName
                }
            });
            onFinalStepAction();
            return;
        }

        goToStep(currentStep + 1);
    };

    useEffect(() => {
        if (lastViewedStepRef.current === currentStep) return;
        lastViewedStepRef.current = currentStep;

        eventTracker.event({
            type: "Churn Statistic Viewed",
            data: {
                workspaceId,
                currentPlan,
                selectedDowngradePlan,
                isDowngrade,
                screen: screens[currentStep].screenName,
                ...(screens[currentStep]?.relativeEventData || {})
            }
        });
    }, [currentStep, screens.length]);

    const handleExtendTrial = async () => {
        setIsExtendingTrial(true);
        await onExtendTrial();
        setIsExtendingTrial(false);
    };

    const handleCancel = () => {
        screens[currentStep].cancelButtonAction();
        onClose();
        onCloseParentModal?.();
    };

    return (
        <motion.div
            className="cancelModal__body"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={LOADER_TRANSITION}
        >
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={`step-${currentStep}`}
                    className="cancelModal__screen"
                    custom={direction}
                    variants={STEP_SCREEN_SLIDE_VARIANTS}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                    {screens[currentStep]?.headerContent}
                    <div className="cancelModal__content">
                        <div className="cancelModal__textContainer">
                            <span className="w-full text-center text-lg font-semibold text-gray-900">
                                {screens[currentStep].heading}
                            </span>
                            <span className="text-tertiary">{screens[currentStep].subheading}</span>
                        </div>
                        <hr />
                        <CheckBox
                            checked={!!checkedBoxes[currentStep]}
                            onChange={() =>
                                setCheckedBoxes((prev) => {
                                    const updated = [...prev];
                                    updated[currentStep] = !prev[currentStep];
                                    return updated;
                                })
                            }
                            label={screens[currentStep]?.checkboxText}
                        />
                        <div className="cancelModal__buttonsContainer">
                            <Button
                                className="flex-1"
                                type="primary"
                                size="medium"
                                onClick={canExtendTrial ? handleExtendTrial : handleCancel}
                                loading={isExtendingTrial}
                            >
                                {canExtendTrial
                                    ? "Extend Trial"
                                    : screens[currentStep].cancelButtonText}
                            </Button>
                            <Button
                                className="flex-1"
                                type="secondary"
                                size="medium"
                                onClick={handleClickNext}
                                disabled={!checkedBoxes[currentStep]}
                            >
                                Yes, I'm sure
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
            <ModalStepsControl
                numberOfSteps={screens.length}
                currentStep={currentStep}
                onStepClick={goToStep}
                checkedBoxes={checkedBoxes}
            />
        </motion.div>
    );
};

interface DefaultCheckboxListProps {
    checkboxes: {
        id: string;
        title: string;
        description: React.ReactNode;
        icon: string;
        count?: number;
    }[];
    acknowledged: Record<string, boolean>;
    toggleAcknowledgment: (id: string) => void;
}

const DefaultCheckboxList = ({
    checkboxes,
    acknowledged,
    toggleAcknowledgment
}: DefaultCheckboxListProps) => (
    <>
        <div className="flex w-full justify-center border-b border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-secondary">
            Your workspace will be subject to the following changes.
        </div>
        <div className="flex w-full flex-col gap-4 p-4 text-sm text-gray-700">
            {checkboxes.map((c) => (
                <CheckBox
                    key={c.id}
                    checked={acknowledged[c.id] || false}
                    onChange={() => toggleAcknowledgment(c.id)}
                    label={
                        <span className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-2">
                                <i className={c.icon}></i>
                                <span className="text-base font-medium">{c.title}</span>
                            </span>
                            {c.count && (
                                <span className="text-base font-semibold">
                                    {c.count.toLocaleString()}
                                </span>
                            )}
                        </span>
                    }
                    description={c.description}
                />
            ))}
        </div>
    </>
);

const LoadingSpinner = () => (
    <motion.div
        className="cancelModal__body"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={LOADER_TRANSITION}
    >
        <div className="flex h-full items-center justify-center">
            <i className="far fa-spinner-third fa-spin text-gray-400"></i>
        </div>
    </motion.div>
);

export function CancelPlanModal({
    usage,
    isOpen,
    onClose,
    onCloseParentModal,
    onNext,
    isDowngrade = false,
    expiredTrial = false,
    selectedDowngradePlan
}: CancelPlanModalProps) {
    const [isLoadingUserStats, setIsLoadingUserStats] = useState(false);
    const isCreditBased = expiredTrial ? true : isCreditSystemEnabled(usage);
    const newPlanLimits = getWorkspacePlanLimits(selectedDowngradePlan, isCreditBased);
    const lastIsOpenRef = useRef(null);

    const { subscription } = usePaidPlanSubscription();

    const currentPlan = usage?.plan;

    const planContentData = usePlanInfo();
    let currentPlanName = planContentData[currentPlan]?.title || "Current Plan";

    const currentPlanIsFree = isFreePlan(currentPlan);
    const currentPlanIsFreeNonSandbox = currentPlanIsFree && currentPlan !== Plan.LegacySandbox;

    const [isLoadingAfterUserCanceled, setIsLoadingAfterUserCanceled] = useState(false);
    const [acknowledged, setAcknowledged] = useState<Record<string, boolean>>({});
    const [churnStats, setChurnStats] = useState<ValueInsightStats | null>(null);
    const fetchInitiatedRef = useRef(false);

    const currentWorkspaceMembers = Object.keys(
        window?.workspacesById?.[window.currentWorkspace]?.members || {}
    ).length;
    const usersToRemove = currentWorkspaceMembers - newPlanLimits.collaborators;

    const rbacEnabled = expiredTrial ? true : !!workspaceSvc.hasRBACAddon(usage);
    const usedTrainCredits = usage?.train?.used;
    const remainingTrainCredits = usage?.train?.quota - usage?.train?.used;
    const apiCallsUsed = usage?.inference?.used;
    const trialLimits = getWorkspacePlanLimits(Plan.UbpPremiumTrial, true);
    const includedCredits = expiredTrial
        ? trialLimits.includedCredits
        : getIncludedCredits({ usage });

    const perCycleCopy = subscription?.isAnnual ? "yr" : "mo";

    const totalImages = (usage.images?.sources || 0) + (usage.images?.transforms || 0);

    const isCancellation =
        selectedDowngradePlan === Plan.Public || selectedDowngradePlan === Plan.LegacySandbox;
    const downgradeMode = isDowngrade && !isCancellation;

    type Callout = {
        title: string;
        id: string;
        criteriaMet?: boolean;
        count?: number;
        icon: string;
        description: React.ReactNode;
    };

    const usageBasedCallouts: Callout[] = [
        {
            title: "Users",
            id: "rbac enabled",
            criteriaMet: rbacEnabled && currentWorkspaceMembers > newPlanLimits.collaborators,
            count: currentWorkspaceMembers,
            icon: "far fa-user fa-fw",
            description: `You will lose access to review mode and labeler permissions. When your subscription ends, you will need to remove ${usersToRemove} ${usersToRemove > 1 ? "users" : "user"}.`
        },
        {
            title: "Users",
            id: "users > 3",
            criteriaMet: currentWorkspaceMembers > newPlanLimits.collaborators && !rbacEnabled,
            count: currentWorkspaceMembers,
            icon: "far fa-user fa-fw",
            description: `When your subscription ends, you will need to remove ${usersToRemove} ${usersToRemove > 1 ? "users" : "user"}.`
        },
        {
            title: "Private Projects",
            id: "private projects",
            criteriaMet:
                selectedDowngradePlan === Plan.Public && !usage?.features?.allowPrivateProjects,
            icon: "far fa-lock fa-fw",
            description: `You will have to make all projects public or lose access to them.`
        },
        {
            title: "Images",
            id: "images > 1,000",
            criteriaMet:
                usage?.images > newPlanLimits.sources && usage?.plan === Plan.LegacyStarter,
            count: usage?.images,
            icon: "far fa-images fa-fw",
            description: `Your Starter Plan includes ${newPlanLimits.sources.toLocaleString()} images. You will be limited to ${newPlanLimits.sources.toLocaleString()} images when your subscription ends.`
        },
        {
            title: "API Calls Used",
            id: "api calls >= 5,000",
            criteriaMet: apiCallsUsed > 5000 && usage?.plan === Plan.LegacyStarter,
            count: apiCallsUsed,
            icon: "far fa-sparkles fa-fw",
            description: `Free plans only include ${newPlanLimits.inferenceQuota.toLocaleString()} API calls/mo. Your Starter Plan includes ${usage?.inference?.quota?.toLocaleString()} API calls/${perCycleCopy} and discounted pricing.`
        },
        {
            title: "Models Trained",
            id: "models trained >= 10",
            criteriaMet: usedTrainCredits >= 10 && usage?.plan === Plan.LegacyStarter,
            count: usedTrainCredits,
            icon: "far fa-coins fa-fw",
            description: `You have trained ${usedTrainCredits?.toLocaleString()} ${usedTrainCredits === 1 ? "model" : "models"} with your Starter Plan. You will have fewer credits when your subscription ends.`
        },
        {
            title: "Training Credits Remaining",
            id: "training credits remaining >= 5",
            criteriaMet:
                remainingTrainCredits >= 5 &&
                !(usedTrainCredits >= 10) &&
                usage?.plan === Plan.LegacyStarter, // don't include if Models Trained callout shown
            count: remainingTrainCredits,
            icon: "far fa-coins fa-fw",
            description: `You will forfeit all ${remainingTrainCredits.toLocaleString()} ${remainingTrainCredits === 1 ? "credit" : "credits"} when your subscription ends.`
        },
        {
            title: "Lose Growth Plan Features",
            id: "growth downgrade",
            criteriaMet:
                usage.plan === Plan.UbpGrowth &&
                (selectedDowngradePlan === Plan.UbpBasic || selectedDowngradePlan === Plan.UbpCore),
            icon: "far fa-arrow-down fa-fw",
            description: `I understand that I will no longer have access to features like Role Based Access Control, Model Monitoring, and Priority Support.`
        },
        {
            title: "Fewer Credits",
            id: "credits",
            criteriaMet: isCreditBased,
            icon: "far fa-coins fa-fw",
            description: `I understand that I will only have access to ${newPlanLimits.includedCredits?.toLocaleString?.() ?? 0} credits/mo.`
        }
    ];

    let checkboxes: Callout[] = usageBasedCallouts.filter((c) => c.criteriaMet);

    if (!downgradeMode || isCancellation) {
        checkboxes.push({
            title: "Public Data",
            id: "public data",
            icon: "far fa-images fa-fw",
            description:
                "I understand that all data and models must be made public to continue using the platform."
        });
    }

    const keepCommercialLicense =
        selectedDowngradePlan === Plan.UbpBasic || selectedDowngradePlan === Plan.UbpCore;
    if (!keepCommercialLicense && !currentPlanIsFreeNonSandbox) {
        checkboxes.push({
            title: "No Commercial Deployment License",
            id: "commercial license",
            icon: "far fa-file-certificate fa-fw",
            description: (
                <>
                    I understand that I will no longer have a commercial license to use downloaded
                    models and weights.{" "}
                    <a href="https://www.roboflow.com/licensing" target="_blank" rel="noopener">
                        Learn More
                    </a>
                </>
            )
        });
    }

    if (
        checkboxes.length === 0 &&
        (usage?.plan === Plan.LegacyStarter || usage?.plan === Plan.UbpGrowth)
    ) {
        checkboxes.push({
            title: "Priority Support",
            id: "support",
            icon: "far fa-comments fa-fw",
            description: "You will lose hands-on support from our team of computer vision experts."
        });
    }

    const allChecked = checkboxes.every((c) => acknowledged[c.id]);

    const toggleAcknowledgment = (id: string) => {
        setAcknowledged((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    useEffect(() => {
        const fetchChurnStats = async () => {
            setIsLoadingUserStats(true);
            try {
                const stats = await getValueInsights(usage.id, { filterEmptyAnnotations: true });
                setChurnStats(stats);
            } catch {
                setChurnStats(null);
            } finally {
                setIsLoadingUserStats(false);
            }
        };

        if (!isOpen) {
            fetchInitiatedRef.current = false;
            return;
        }

        if (fetchInitiatedRef.current) return;

        fetchInitiatedRef.current = true;
        fetchChurnStats();
        setAcknowledged({});
    }, [isOpen, usage.id]);

    const handleNext = async () => {
        setIsLoadingAfterUserCanceled(true);
        await onNext();
        setIsLoadingAfterUserCanceled(false);
    };

    const isOnTrialPlan =
        currentPlan === Plan.UbpPremiumTrial || currentPlan === Plan.LegacySandbox;
    // Is on trial plan && trial is expired && trial end of trial flow has not been handled
    const isTrialExpired =
        isOnTrialPlan && usage?.trial?.isTrial == false && usage?.trial?.endOfTrialHandled == false;
    const hasExtendedTrial = usage?.trial?.extendedTrial;
    const canExtendTrial = isTrialExpired && !hasExtendedTrial;
    // Warn legacy plans that they won't be able to re-enroll
    const isLegacyPlan = !PubliclyAvailablePlans.includes(currentPlan as Plan) && !isOnTrialPlan;

    const userStatisticScreensToShow = useMemo(
        () =>
            getRelevantUserStatisticScreens({
                churnStats,
                onFinalStepAction: handleNext,
                workspaceId: usage.id,
                currentPlan,
                selectedDowngradePlan,
                isDowngrade
            }),
        [churnStats]
    );
    const hasUserStatistics = userStatisticScreensToShow.length > 0;

    useEffect(() => {
        if (isOpen === lastIsOpenRef.current) return;
        lastIsOpenRef.current = isOpen;

        if (isOpen) {
            eventTracker.event({
                type: "Churn Modal Opened",
                data: {
                    workspaceId: usage.id,
                    currentPlan,
                    selectedDowngradePlan,
                    isDowngrade,
                    hasUserStatistics,
                    screens: hasUserStatistics
                        ? userStatisticScreensToShow.map((s) => s.screenName)
                        : checkboxes.map((c) => c.id)
                }
            });
        }
    }, [isOpen]);

    const handleClose = () => {
        eventTracker.event({
            type: "Churn Modal Closed",
            data: {
                workspaceId: usage.id,
                currentPlan,
                selectedDowngradePlan,
                isDowngrade,
                hasUserStatistics,
                screens: hasUserStatistics
                    ? userStatisticScreensToShow.map((s) => s.screenName)
                    : checkboxes.map((c) => c.id)
            }
        });
        onClose();
    };

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={handleClose}
            title={`Are you sure? You’ve made good use of your ${currentPlanName}`}
            icon="far fa-exclamation-triangle"
            cancelButtonText="Nevermind"
            submitButtonText={
                isCancellation ? "Cancel Plan" : downgradeMode ? "Downgrade Plan" : "Cancel Plan"
            }
            type="warning"
            onSubmit={handleNext}
            submitButtonProps={{
                loading: isLoadingAfterUserCanceled,
                type: "red",
                disabled: !allChecked
            }}
            cancelButtonProps={{ disabled: !!isLoadingAfterUserCanceled }}
            showFooter={!isLoadingUserStats && !hasUserStatistics}
        >
            <div className="flex">
                <div className="flex w-full flex-1 flex-col items-center rounded-lg">
                    {isLegacyPlan && <LegacyPlanWarning currentPlanName={currentPlanName} />}
                    <AnimatePresence>
                        {!isLoadingUserStats && hasUserStatistics && (
                            <UserStatisticsFlow
                                screens={userStatisticScreensToShow}
                                onFinalStepAction={handleNext}
                                workspaceId={usage.id}
                                currentPlan={currentPlan}
                                selectedDowngradePlan={selectedDowngradePlan}
                                isDowngrade={isDowngrade}
                                canExtendTrial={canExtendTrial}
                                onExtendTrial={() => handleExtendTrial(usage.id)}
                                onClose={handleClose}
                                onCloseParentModal={onCloseParentModal}
                            />
                        )}
                        {!isLoadingUserStats && !hasUserStatistics && (
                            <DefaultCheckboxList
                                checkboxes={checkboxes}
                                acknowledged={acknowledged}
                                toggleAcknowledgment={toggleAcknowledgment}
                            />
                        )}
                        {isLoadingUserStats && <LoadingSpinner />}
                    </AnimatePresence>
                </div>
            </div>
        </ModalWithIcon>
    );
}
